import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { resolveImageUrl } from '../lib/api';
import { useLang } from '../hooks/useLang';
import { pickLocalized } from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PhilosophySection = () => {
  const { t } = useTranslation();
  const { lang } = useLang();
  const [philosophy, setPhilosophy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPhilosophy = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/philosophy`);
        if (!response.ok) throw new Error('Failed to fetch philosophy');
        const data = await response.json();
        setPhilosophy(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching philosophy:', err);
        setError(err.message);
        setLoading(false);
      }
    };
    fetchPhilosophy();
  }, []);

  if (loading) {
    return (
      <section className="py-24 px-8 lg:px-16 bg-[#f5f2ed]">
        <div className="max-w-[1920px] mx-auto"><p className="text-center text-gray-500">{t('common.loading')}</p></div>
      </section>
    );
  }
  if (error || !philosophy) return null;

  const title = pickLocalized(philosophy, 'title', lang);
  const subtitle = pickLocalized(philosophy, 'subtitle', lang);
  const description = pickLocalized(philosophy, 'description', lang);
  const fullText = pickLocalized(philosophy, 'fullText', lang);

  return (
    <section className="py-24 px-8 lg:px-16 bg-[#f5f2ed]">
      <div className="max-w-[1920px] mx-auto">
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          <div className="w-full lg:w-1/2">
            <div className="overflow-hidden bg-white">
              <img src={resolveImageUrl(philosophy.image)} alt={title} className="w-full h-auto object-contain" />
            </div>
          </div>
          <div className="w-full lg:w-1/2">
            <h3 className="text-sm font-light tracking-wider text-gray-500 mb-4 uppercase">{subtitle}</h3>
            <h2 className="text-4xl lg:text-5xl font-light mb-8 leading-tight">{title}</h2>
            <p className="text-base font-light text-gray-700 leading-relaxed mb-6">{description}</p>
            <p className="text-base font-light text-gray-700 leading-relaxed mb-8">{fullText}</p>
            <a href={philosophy.link}
              className="inline-flex items-center gap-2 text-sm font-light tracking-wide text-[#d94e47] hover:gap-4 transition-all duration-300 border-b border-[#d94e47]">
              {t('common.discoverPhilosophy')}
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PhilosophySection;
